import { cn } from "@babybjorn/ui";
import {
	Children,
	useCallback,
	useEffect,
	useRef,
	useState,
	type FC,
	type PropsWithChildren,
} from "react";
import Button from "../../components/general/Button";
import Scrollbar from "../general/Scrollbar";
import * as s from "./ProductCardSlider.module.scss";

type ProductCardSliderProps = PropsWithChildren<{
	label?: string | null;
	title?: string | null;
	ariaLabelNext?: string;
	ariaLabelPrev?: string;
	scrollbarLength?: number;
}>;

const ProductCardSlider: FC<ProductCardSliderProps> = ({
	label,
	title,
	ariaLabelNext = "Next slide",
	ariaLabelPrev = "Previous slide",
	scrollbarLength = 0,
	children,
}) => {
	const listRef = useRef<HTMLUListElement>(null);
	const [isAtStart, setIsAtStart] = useState(true);
	const [isAtEnd, setIsAtEnd] = useState(false);

	const checkScrollPosition = useCallback(() => {
		if (!listRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = listRef.current;
		setIsAtStart(scrollLeft <= 0);
		setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
	}, []);

	const scroll = useCallback((direction: "left" | "right") => {
		if (!listRef.current?.children[0]) return;

		const scrollAmount = listRef.current.children[0].clientWidth;
		listRef.current.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	}, []);

	const onScrollLeft = useCallback(() => scroll("left"), [scroll]);
	const onScrollRight = useCallback(() => scroll("right"), [scroll]);

	useEffect(() => {
		const list = listRef.current;
		if (!list) return;

		list.addEventListener("scroll", checkScrollPosition);
		window.addEventListener("resize", checkScrollPosition);
		checkScrollPosition();

		return () => {
			list.removeEventListener("scroll", checkScrollPosition);
			window.removeEventListener("resize", checkScrollPosition);
		};
	}, [checkScrollPosition]);

	const childCount = Children.count(children);

	return (
		<section
			className={s.productCardSlider}
			aria-label={label || "Product slider"}
		>
			{label && <label className={s.label}>{label}</label>}
			<div className={s.titleContainer}>
				{title && <h2>{title}</h2>}
				<div
					className={cn(s.buttonContainer, {
						[s.hidden]: isAtStart && isAtEnd,
					})}
				>
					<Button
						onClick={onScrollLeft}
						icon="arrow-left"
						variant="outlined"
						buttonType="icon"
						aria-label={ariaLabelPrev}
						disabled={isAtStart}
						aria-controls="product-slider"
					/>
					<Button
						onClick={onScrollRight}
						icon="arrow-right"
						variant="outlined"
						buttonType="icon"
						aria-label={ariaLabelNext}
						disabled={isAtEnd}
						aria-controls="product-slider"
					/>
				</div>
			</div>
			{scrollbarLength > 0 ? (
				<Scrollbar
					className={s.slider}
					ariaLabelScroll="scroll"
					slider
					scrollbarLength={scrollbarLength}
				>
					{children}
				</Scrollbar>
			) : (
				<ul
					ref={listRef}
					className={s.slider}
					style={
						{
							"--item-count": childCount,
						} as React.CSSProperties
					}
					aria-labelledby={label ? "slider-label" : "slider-title"}
				>
					{children}
				</ul>
			)}
		</section>
	);
};

export default ProductCardSlider;
